import { useEffect, useMemo, useState } from "react";
import type { ContactInput, ContactRole, ContactoTarget } from "@career-ops-amplify/shared";
import { parseContactoOutput } from "@/lib/contacto-parser";
import { useBatchCreateContacts } from "@/hooks/useContacts";

interface ContactoSavePanelProps {
  output: string;
  companySlug: string;
  jobId?: string;
  onSaved?: () => void;
}

function toContactInput(
  target: ContactoTarget,
  companySlug: string,
  jobId?: string,
): ContactInput {
  return {
    companySlug,
    name: target.name,
    email: target.email,
    linkedinUrl: target.linkedinUrl,
    role: (target.role as ContactRole | undefined) ?? "recruiter",
    title: target.title,
    source: "contacto",
    notes: target.rationale,
    jobIds: jobId ? [jobId] : undefined,
  };
}

export function ContactoSavePanel({
  output,
  companySlug,
  jobId,
  onSaved,
}: ContactoSavePanelProps) {
  const batchCreate = useBatchCreateContacts();
  const parsed = useMemo(() => parseContactoOutput(output), [output]);
  const [targets, setTargets] = useState<ContactoTarget[]>([]);
  const [error, setError] = useState("");

  useEffect(() => {
    setTargets(parsed.map((t) => ({ ...t, selected: true })));
  }, [parsed]);

  if (parsed.length === 0) {
    return (
      <p className="text-xs text-muted-foreground">
        No structured targets found in contacto output. Add contacts manually below.
      </p>
    );
  }

  const selected = targets.filter((t) => t.selected);

  function toggleIndex(index: number) {
    setTargets((prev) =>
      prev.map((t, i) => (i === index ? { ...t, selected: !t.selected } : t)),
    );
  }

  async function handleSave() {
    if (selected.length === 0) {
      setError("Select at least one contact to save.");
      return;
    }
    setError("");
    await batchCreate.mutateAsync(
      selected.map((t) => toContactInput(t, companySlug, jobId)),
    );
    onSaved?.();
  }

  return (
    <div className="mt-3 rounded-md border border-dashed border-brand/40 bg-brand/5 p-3">
      <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        Save discovered contacts
      </h4>
      <ul className="space-y-2">
        {targets.map((t, i) => (
          <li key={`${t.name}-${i}`} className="flex items-start gap-2 text-sm">
            <input
              type="checkbox"
              checked={!!t.selected}
              onChange={() => toggleIndex(i)}
              className="mt-1"
            />
            <div>
              <p className="font-medium text-foreground">{t.name}</p>
              <p className="text-xs text-muted-foreground">
                {[t.role, t.title].filter(Boolean).join(" · ")}
              </p>
              {t.rationale && (
                <p className="mt-0.5 text-xs text-muted-foreground">{t.rationale}</p>
              )}
            </div>
          </li>
        ))}
      </ul>
      {error && <p className="mt-2 text-xs text-red-500">{error}</p>}
      <button
        type="button"
        onClick={handleSave}
        disabled={batchCreate.isPending || selected.length === 0}
        className="mt-3 rounded-md bg-brand px-3 py-1.5 text-xs font-medium text-brand-foreground disabled:opacity-50"
      >
        {batchCreate.isPending
          ? "Saving…"
          : `Save ${selected.length} contact${selected.length === 1 ? "" : "s"}`}
      </button>
    </div>
  );
}
